import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  TableSortLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import reservationService from '../services/reservationService';
import { useLanguage } from '../App';

// Reservations translations
const reservationsTranslations = {
  en: {
    reservations_title: 'Reservations',
    new_reservation: 'New Reservation',
    search_placeholder: 'Search reservations...',
    search_by_guest: 'Guest Name',
    search_by_property: 'Property',
    search_by_date: 'Check-in Date',
    refresh: 'Refresh',
    confirmation_code: 'Confirmation',
    guest_name: 'Guest Name',
    property: 'Property',
    check_in: 'Check-in',
    check_out: 'Check-out',
    guests: 'Guests',
    status: 'Status',
    actions: 'Actions',
    view: 'View',
    edit: 'Edit',
    delete: 'Delete',
    loading_reservations: 'Loading reservations...',
    error_loading: 'Error loading reservations',
    no_reservations: 'No reservations found',
    delete_confirmation: 'Delete Reservation',
    delete_message: 'Are you sure you want to delete this reservation?',
    cancel: 'Cancel',
    guest_info: 'Guest Info'
  },
  pt: {
    reservations_title: 'Reservas',
    new_reservation: 'Nova Reserva',
    search_placeholder: 'Buscar reservas...',
    search_by_guest: 'Nome do Hóspede',
    search_by_property: 'Propriedade',
    search_by_date: 'Data Check-in',
    refresh: 'Atualizar',
    confirmation_code: 'Confirmação',
    guest_name: 'Nome do Hóspede',
    property: 'Propriedade',
    check_in: 'Check-in',
    check_out: 'Check-out',
    guests: 'Hóspedes',
    status: 'Status',
    actions: 'Ações',
    view: 'Ver',
    edit: 'Editar',
    delete: 'Excluir',
    loading_reservations: 'Carregando reservas...',
    error_loading: 'Erro ao carregar reservas',
    no_reservations: 'Nenhuma reserva encontrada',
    delete_confirmation: 'Excluir Reserva',
    delete_message: 'Tem certeza que deseja excluir esta reserva?',
    cancel: 'Cancelar',
    guest_info: 'Info Hóspede'
  },
  es: {
    reservations_title: 'Reservas',
    new_reservation: 'Nueva Reserva',
    search_placeholder: 'Buscar reservas...',
    search_by_guest: 'Nombre del Huésped',
    search_by_property: 'Propiedad',
    search_by_date: 'Fecha Check-in',
    refresh: 'Actualizar',
    confirmation_code: 'Confirmación',
    guest_name: 'Nombre del Huésped',
    property: 'Propiedad',
    check_in: 'Check-in',
    check_out: 'Check-out',
    guests: 'Huéspedes',
    status: 'Estado',
    actions: 'Acciones',
    view: 'Ver',
    edit: 'Editar',
    delete: 'Eliminar',
    loading_reservations: 'Cargando reservas...',
    error_loading: 'Error al cargar reservas',
    no_reservations: 'No se encontraron reservas',
    delete_confirmation: 'Eliminar Reserva',
    delete_message: '¿Está seguro que desea eliminar esta reserva?',
    cancel: 'Cancelar',
    guest_info: 'Info Huésped'
  }
};

function ReservationList() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('guestName');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, reservation: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [orderBy, setOrderBy] = useState('checkInDate');
  const [order, setOrder] = useState('desc');
  const navigate = useNavigate();
  const { language } = useLanguage();

  const t = (key) => {
    return reservationsTranslations[language]?.[key] || reservationsTranslations.en[key] || key;
  };

  useEffect(() => {
    loadReservations();
  }, []);

  const loadReservations = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await reservationService.getReservations(filters);
      setReservations(response.reservations || []);
    } catch (err) {
      setSnackbar({ open: true, message: `${t('error_loading')}: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchTerm.trim()) {
      try {
        setLoading(true);
        setError(null);
        const response = await reservationService.searchReservations(searchTerm.trim(), searchType);
        setReservations(response.reservations || []);
        setSnackbar({ open: true, message: `${response.reservations?.length || 0} reservas encontradas`, severity: 'info' });
      } catch (err) {
        setSnackbar({ open: true, message: `${t('error_loading')}: ${err.message}`, severity: 'error' });
      } finally {
        setLoading(false);
      }
    } else {
      await loadReservations();
    }
  };

  const handleRefresh = () => {
    setSearchTerm('');
    loadReservations();
  };

  const handleDelete = async () => {
    try {
      await reservationService.deleteReservation(deleteDialog.reservation.confirmationCode);
      setDeleteDialog({ open: false, reservation: null });
      loadReservations();
      setSnackbar({ open: true, message: 'Reserva excluída com sucesso!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `${t('error_deleting')}: ${err.message}`, severity: 'error' });
    }
  };

  const handleForceAccessCreation = async (reservation) => {
    try {
      setLoading(true);
      
      // Update reservation status to trigger daily actions automation
      // Use "Ready for Integration" status which is monitored by automation
      const updateData = {
        ...reservation,
        status: 'Ready for Integration',
        forceAccessCreation: true,
        updatedAt: new Date().toISOString()
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/reservations`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        setSnackbar({ 
          open: true, 
          message: `Status updated to "Ready for Integration" - Access will be created automatically by the system within 10 minutes.`, 
          severity: 'success' 
        });
        
        // Reload reservations to show updated status
        loadReservations();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update reservation status');
      }
    } catch (err) {
      console.error('Force access creation error:', err);
      setSnackbar({ 
        open: true, 
        message: `Failed to update status: ${err.message}`, 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'booking confirmed':
        return 'success';
      case 'checked in':
        return 'info';
      case 'checked out':
        return 'default';
      case 'cancelled':
        return 'warning';
      case 'deleted':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle different date formats and ensure consistent parsing
      let date;
      if (dateString.includes('-')) {
        // YYYY-MM-DD format - parse without timezone conversion
        const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
        date = new Date(year, month - 1, day);
      } else {
        // Fallback for other formats
        date = new Date(dateString);
      }
      
      // Return formatted date
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      console.warn('Date formatting error:', error, 'for date:', dateString);
      return dateString;
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedReservations = React.useMemo(() => {
    return [...reservations].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      // Handle guest name sorting
      if (orderBy === 'guestName') {
        aValue = `${a.guestFirstName || ''} ${a.guestLastName || ''}`.trim();
        bValue = `${b.guestFirstName || ''} ${b.guestLastName || ''}`.trim();
      }
      
      // Handle date sorting
      if (orderBy === 'checkInDate' || orderBy === 'checkOutDate') {
        aValue = new Date(aValue || '1970-01-01');
        bValue = new Date(bValue || '1970-01-01');
      }
      
      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [reservations, order, orderBy]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('reservations_title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/reservations/new')}
        >
          {t('new_reservation')}
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Buscar Por</InputLabel>
            <Select
              value={searchType}
              label="Buscar Por"
              onChange={(e) => setSearchType(e.target.value)}
            >
              <MenuItem value="guestName">{t('search_by_guest')}</MenuItem>
              <MenuItem value="listingNickname">{t('search_by_property')}</MenuItem>
              <MenuItem value="checkInDate">{t('search_by_date')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder={t('search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Buscar
          </Button>
          <IconButton onClick={handleRefresh} title={t('refresh')}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{t('loading_reservations')}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'confirmationCode'}
                    direction={orderBy === 'confirmationCode' ? order : 'asc'}
                    onClick={() => handleRequestSort('confirmationCode')}
                  >
                    {t('confirmation_code')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'guestName'}
                    direction={orderBy === 'guestName' ? order : 'asc'}
                    onClick={() => handleRequestSort('guestName')}
                  >
                    {t('guest_name')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'listingNickname'}
                    direction={orderBy === 'listingNickname' ? order : 'asc'}
                    onClick={() => handleRequestSort('listingNickname')}
                  >
                    {t('property')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'checkInDate'}
                    direction={orderBy === 'checkInDate' ? order : 'asc'}
                    onClick={() => handleRequestSort('checkInDate')}
                  >
                    {t('check_in')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'checkOutDate'}
                    direction={orderBy === 'checkOutDate' ? order : 'asc'}
                    onClick={() => handleRequestSort('checkOutDate')}
                  >
                    {t('check_out')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    {t('status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'channel'}
                    direction={orderBy === 'channel' ? order : 'asc'}
                    onClick={() => handleRequestSort('channel')}
                  >
                    Canal
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedReservations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">
                      {t('no_reservations')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedReservations.map((reservation) => (
                  <TableRow key={reservation.confirmationCode}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {reservation.confirmationCode}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {reservation.guestFirstName} {reservation.guestLastName}
                      {reservation.guestPhone && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {reservation.guestPhone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {reservation.listingNickname || reservation.listingTitle || 'N/A'}
                    </TableCell>
                    <TableCell>{formatDate(reservation.checkInDate)}</TableCell>
                    <TableCell>{formatDate(reservation.checkOutDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={reservation.status || 'Unknown'}
                        size="small"
                        color={getStatusColor(reservation.status)}
                      />
                    </TableCell>
                    <TableCell>{reservation.channel || 'N/A'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/reservations/${reservation.confirmationCode}`)}
                        title={t('view')}
                      >
                        <ViewIcon />
                      </IconButton>
                      {(reservation.guestInformation || reservation.partialGuestInformation || reservation.status === 'Guest Information Provided') && (
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/reservations/${reservation.confirmationCode}/guest-info`)}
                          title={t('guest_info')}
                          color="primary"
                        >
                          <ViewIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/reservations/${reservation.confirmationCode}/edit`)}
                        title={t('edit')}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleForceAccessCreation(reservation)}
                        title="Force Access Creation"
                        disabled={loading}
                      >
                        <KeyIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, reservation })}
                        title={t('delete')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, reservation: null })}
      >
        <DialogTitle>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('delete_message')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>
              {deleteDialog.reservation?.guestFirstName} {deleteDialog.reservation?.guestLastName}
            </strong>{' '}
            (Confirmação: {deleteDialog.reservation?.confirmationCode})
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Esta ação marcará a reserva como excluída, mas não a removerá permanentemente do sistema.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, reservation: null })}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ReservationList;
